import React, { useState, useEffect, useRef, useCallback } from "react";
import "./Bbs.css";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import * as noticeFormatDate from "../../../../components/Util/DateFormat";
import { getServerBaseUrl } from "../../../../utils/portConfig";
import PatientCommunity from "./PatientCommunity";

function Bbs({ fromMokLok }) {
  const [communityData, setCommunityData] = useState([]); //커뮤니티 탭
  const [noticeData, setNoticeData] = useState([]); // 공지 탭
  const [voteData, setVoteData] = useState([]); // 투표 탭
  const [selectedTab, setSelectedTab] = useState(fromMokLok ? fromMokLok : "공지"); // 선택된 탭

  // 무한 스크롤 관련 상태 추가
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // 각 탭별 hasMore 상태 추가
  const [noticeHasMore, setNoticeHasMore] = useState(true);
  const [communityHasMore, setCommunityHasMore] = useState(true);
  const [voteHasMore, setVoteHasMore] = useState(true);

  // 클라이언트 측 페이징을 위한 상태
  const [displayedNoticeData, setDisplayedNoticeData] = useState([]);
  const [displayedCommunityData, setDisplayedCommunityData] = useState([]);
  const [displayedVoteData, setDisplayedVoteData] = useState([]);

  // 한 번에 보여줄 아이템 수
  const ITEMS_PER_LOAD = 5;

  // 데이터 타입별 페이지 정보
  const [noticePageInfo, setNoticePageInfo] = useState({ page: 1, hasMore: true });
  const [communityPageInfo, setCommunityPageInfo] = useState({ page: 1, hasMore: true });
  const [votePageInfo, setVotePageInfo] = useState({ page: 1, hasMore: true });

  // right-group 참조 생성
  const rightGroupRef = useRef(null);
  const observer = useRef();

  const API_SERVER_URL = `${getServerBaseUrl()}/api/v1/bbs`; // API 서버 URL
  const API_VOTE_SERVER_URL = `${getServerBaseUrl()}/api/v1/vote`; // API 서버 URL(vote)
  const token = localStorage.getItem("token"); // 토큰
  const userId = localStorage.getItem("id");

  const navigate = useNavigate();

  // 무한 스크롤을 위한 마지막 아이템 참조 설정
  const lastElementRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        // 현재 탭에 맞는 hasMore 상태 확인
        let currentHasMore = false;
        switch (selectedTab) {
          case "공지":
            currentHasMore = noticeHasMore;
            break;
          case "커뮤니티":
            currentHasMore = communityHasMore;
            break;
          case "투표목록":
            currentHasMore = voteHasMore;
            break;
          default:
            return;
        }

        if (entries[0].isIntersecting && currentHasMore) {
          // 스크롤이 마지막 요소에 도달하면 다음 데이터 로드
          loadMoreData();
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, noticeHasMore, communityHasMore, voteHasMore, selectedTab]
  );

  // 스크롤 시 추가 데이터 로드 함수
  const loadMoreData = () => {
    // 현재 탭에 맞는 hasMore 확인
    let currentHasMore = false;
    switch (selectedTab) {
      case "공지":
        currentHasMore = noticeHasMore;
        break;
      case "커뮤니티":
        currentHasMore = communityHasMore;
        break;
      case "투표목록":
        currentHasMore = voteHasMore;
        break;
      default:
        return;
    }

    if (loading || !currentHasMore) return;
    setLoading(true);

    switch (selectedTab) {
      case "공지":
        if (displayedNoticeData.length >= noticeData.length) {
          setNoticeHasMore(false);
          setLoading(false);
          return;
        }

        const nextNoticeItems = noticeData.slice(displayedNoticeData.length, displayedNoticeData.length + ITEMS_PER_LOAD);

        setDisplayedNoticeData((prev) => [...prev, ...nextNoticeItems]);
        setNoticePageInfo((prev) => ({
          page: prev.page + 1,
          hasMore: displayedNoticeData.length + nextNoticeItems.length < noticeData.length,
        }));
        setNoticeHasMore(displayedNoticeData.length + nextNoticeItems.length < noticeData.length);
        break;

      case "커뮤니티":
        if (displayedCommunityData.length >= communityData.length) {
          setCommunityHasMore(false);
          setLoading(false);
          return;
        }

        const nextCommunityItems = communityData.slice(displayedCommunityData.length, displayedCommunityData.length + ITEMS_PER_LOAD);

        setDisplayedCommunityData((prev) => [...prev, ...nextCommunityItems]);
        setCommunityPageInfo((prev) => ({
          page: prev.page + 1,
          hasMore: displayedCommunityData.length + nextCommunityItems.length < communityData.length,
        }));
        setCommunityHasMore(displayedCommunityData.length + nextCommunityItems.length < communityData.length);
        break;

      case "투표목록":
        if (displayedVoteData.length >= voteData.length) {
          setVoteHasMore(false);
          setLoading(false);
          return;
        }

        const nextVoteItems = voteData.slice(displayedVoteData.length, displayedVoteData.length + ITEMS_PER_LOAD);

        setDisplayedVoteData((prev) => [...prev, ...nextVoteItems]);
        setVotePageInfo((prev) => ({
          page: prev.page + 1,
          hasMore: displayedVoteData.length + nextVoteItems.length < voteData.length,
        }));
        setVoteHasMore(displayedVoteData.length + nextVoteItems.length < voteData.length);
        break;

      default:
        setLoading(false);
        return;
    }

    // 데이터 로드 후 로딩 상태 해제 (지연 추가)
    setTimeout(() => {
      setLoading(false);
    }, 300);
  };

  // 초기 데이터 로드
  useEffect(() => {
    // 탭이 변경될 때마다 해당 탭의 초기 데이터 로드
    setLoading(true);

    // 데이터 초기화 및 첫 페이지 로드
    fetchInitialData();
  }, [selectedTab]);

  // 초기 데이터 로드 함수
  const fetchInitialData = async () => {
    try {
      switch (selectedTab) {
        case "공지":
          // 탭 변경 시 항상 데이터를 새로 로드
          const noticeResponse = await axios.get(`${API_SERVER_URL}/notice/list`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (noticeResponse.data) {
            setNoticeData(noticeResponse.data);
            // 초기 표시 데이터 설정
            const noticeInitialItems = noticeResponse.data.slice(0, ITEMS_PER_LOAD);
            setDisplayedNoticeData(noticeInitialItems);
            setNoticePageInfo({
              page: 1,
              hasMore: noticeResponse.data.length > ITEMS_PER_LOAD,
            });
            setNoticeHasMore(noticeResponse.data.length > ITEMS_PER_LOAD);
          }
          break;

        case "커뮤니티":
          // 탭 변경 시 항상 데이터를 새로 로드
          const communityResponse = await axios.get(`${API_SERVER_URL}/community/list`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (communityResponse.data) {
            setCommunityData(communityResponse.data);
            // 초기 표시 데이터 설정
            const communityInitialItems = communityResponse.data.slice(0, ITEMS_PER_LOAD);
            setDisplayedCommunityData(communityInitialItems);
            setCommunityPageInfo({
              page: 1,
              hasMore: communityResponse.data.length > ITEMS_PER_LOAD,
            });
            setCommunityHasMore(communityResponse.data.length > ITEMS_PER_LOAD);
          }
          break;

        case "투표목록":
          // 탭 변경 시 항상 데이터를 새로 로드
          const voteResponse = await axios.get(`${API_VOTE_SERVER_URL}/list`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (voteResponse.data) {
            setVoteData(voteResponse.data);
            // 초기 표시 데이터 설정
            const voteInitialItems = voteResponse.data.slice(0, ITEMS_PER_LOAD);
            setDisplayedVoteData(voteInitialItems);
            setVotePageInfo({
              page: 1,
              hasMore: voteResponse.data.length > ITEMS_PER_LOAD,
            });
            setVoteHasMore(voteResponse.data.length > ITEMS_PER_LOAD);
          }
          break;
      }
    } catch (error) {
      console.error(`${selectedTab} 초기 데이터 가져오기 실패:`, error);
    } finally {
      setLoading(false);
    }
  };

  // 탭을 클릭했을 때 호출되는 함수
  const handleTabClick = (tabName) => {
    if (selectedTab === tabName) return; // 같은 탭 클릭 시 무시

    setSelectedTab(tabName); // 선택된 탭을 업데이트

    // 탭 변경 시 해당 탭의 표시 데이터를 초기화
    switch (tabName) {
      case "공지":
        setDisplayedNoticeData([]);
        setNoticePageInfo({ page: 1, hasMore: true });
        setNoticeHasMore(true);
        break;
      case "커뮤니티":
        setDisplayedCommunityData([]);
        setCommunityPageInfo({ page: 1, hasMore: true });
        setCommunityHasMore(true);
        break;
      case "투표목록":
        setDisplayedVoteData([]);
        setVotePageInfo({ page: 1, hasMore: true });
        setVoteHasMore(true);
        break;
    }
  };

  const movingNoticeRegisterPage = () => {
    navigate("/", { state: { setFromMokLok: "등록" } });
  };

  const movingVotePage = () => {
    navigate("/", { state: { setFromMokLok: "투표" } });
  };

  const navigateTo = (tab, mode, itemId, viewsCount, regDate) => {
    const fromMokLok = mode === "커뮤니티" ? "상세(Community)" : "상세(Announce)";
    navigate("/", { state: { setFromMokLok: fromMokLok, setNoticeDetailId: itemId, isCommunity: selectedTab, setViewsCount: viewsCount, setBbsDate: regDate } });
  };

  const navigateToVoteDetail = (tab, mode, itemId, regDate) => {
    const fromMokLok = "투표상세";
    navigate("/", { state: { setFromMokLok: fromMokLok, setBbsDate: regDate, setNoticeDetailId: itemId } });
  };

  return (
    <div className="bbs-content-container">
      <div className="bbs-tab-content">
        <div className="right-group" ref={rightGroupRef}>
          <div className="tab-area">
            <div className={`tab ${selectedTab === "공지" ? "selected-tab" : "not-selected-tab"}`} onClick={() => handleTabClick("공지")}>
              공지
            </div>
            <div className={`tab ${selectedTab === "커뮤니티" ? "selected-tab" : "not-selected-tab"}`} onClick={() => handleTabClick("커뮤니티")}>
              커뮤니티
            </div>
            <div className={`tab ${selectedTab === "환자_커뮤니티" ? "selected-tab" : "not-selected-tab"}`} onClick={() => handleTabClick("환자_커뮤니티")}>
              환자 커뮤니티
            </div>
            <div className={`tab ${selectedTab === "투표목록" ? "selected-tab" : "not-selected-tab"}`} onClick={() => handleTabClick("투표목록")}>
              투표
            </div>
          </div>
          {selectedTab === "공지" && (
            <div className="bbs-table-container" style={{ maxHeight: "calc(100vh - 128px)" }}>
              <div className="bbs-custom-table">
                <div className="row header3 bbs-header-box">
                  <div className="cell">번호</div>
                  <div className="cell-title">제목</div>
                  <div className="cell">작성자</div>
                  <div className="cell">작성일</div>
                  <div className="cell">조회 수</div>
                </div>
                {displayedNoticeData.map((announceItem, index) => (
                  <div
                    className={`${index % 2 === 0 ? "row row-hovered" : "row-colored bbs-click-style row-hovered"}`}
                    key={index}
                    ref={index === displayedNoticeData.length - 1 ? lastElementRef : null}
                  >
                    <div className="cell text-center">{`${announceItem.id}`}</div>
                    <div
                      className="cell bbs-click-style"
                      onClick={() => navigateTo("공지", "공지", `${announceItem.id}`, `${announceItem.viewsCount}`, `${announceItem.regDate}`)}
                    >{`${announceItem.title}`}</div>
                    <div className="cell text-center">{`${announceItem.regUser}`}</div>
                    <div className="cell text-center">{`${noticeFormatDate.formatDateWbar(announceItem.regDate) || ""}`}</div>
                    <div className="cell text-right">{`${announceItem.viewsCount}`}</div>
                  </div>
                ))}
              </div>
              {loading && (
                <div className="loading-indicator">
                  <div>로딩 중...</div>
                </div>
              )}
            </div>
          )}
          {selectedTab === "커뮤니티" && (
            <div className="bbs-table-container" style={{ maxHeight: "calc(100vh - 166px)" }}>
              <div className="bbs-custom-table">
                <div className="row header2 bbs-header-box">
                  <div className="cell">번호</div>
                  <div className="cell-title">제목</div>
                  <div className="cell">작성자</div>
                  <div className="cell">작성일</div>
                  <div className="cell">조회 수</div>
                </div>
                {displayedCommunityData.map((communityItem, index) => (
                  <div
                    className={`${index % 2 === 0 ? "row row-hovered" : "row-colored bbs-click-style row-hovered"}`}
                    key={index}
                    ref={index === displayedCommunityData.length - 1 ? lastElementRef : null}
                  >
                    <div className="cell text-center">{`${communityItem.id}`}</div>
                    <div
                      className="cell bbs-click-style"
                      onClick={() => navigateTo("커뮤니티", "커뮤니티", `${communityItem.id}`, `${communityItem.viewsCount}`, `${communityItem.regDate}`)}
                    >{`${communityItem.title}`}</div>
                    <div className="cell text-center">{`${communityItem.regUser}`}</div>
                    <div className="cell text-center">{`${noticeFormatDate.formatDateWbar(communityItem.regDate) || ""}`}</div>
                    <div className="cell text-right">{`${communityItem.viewsCount}`}</div>
                  </div>
                ))}
              </div>
              {loading && (
                <div className="loading-indicator">
                  <div>로딩 중...</div>
                </div>
              )}
              <div className="bbs-footer-container" style={{ bottom: "56px" }}>
                <div className="bbs-button-container">
                  <button onClick={movingNoticeRegisterPage}>등록</button>
                </div>
              </div>
            </div>
          )}
          {selectedTab === "환자_커뮤니티" && <PatientCommunity fromMokLok={fromMokLok} />}
          {selectedTab === "투표목록" && (
            <div className="bbs-table-container" style={{ maxHeight: "calc(100vh - 166px)" }}>
              <div className="bbs-custom-table">
                <div className="row header bbs-header-box">
                  <div className="cell">번호</div>
                  <div className="cell-title">제목</div>
                  <div className="cell">작성자</div>
                  <div className="cell">작성일</div>
                </div>
                {displayedVoteData.map((voteItem, index) => (
                  <div
                    className={`${index % 2 === 0 ? "row row-hovered" : "row-colored bbs-click-style row-hovered"}`}
                    key={index}
                    ref={index === displayedVoteData.length - 1 ? lastElementRef : null}
                  >
                    <div className="cell text-center">{`${voteItem.id}`}</div>
                    <div className="cell bbs-click-style" onClick={() => navigateToVoteDetail("투표", "투표", `${voteItem.id}`, `${voteItem.regDate}`)}>
                      {voteItem.enabled === false ? (
                        <div className="title-style">
                          <div className="icon-over">종료</div>
                          {voteItem.title}
                        </div>
                      ) : (
                        `${voteItem.title}`
                      )}
                    </div>
                    <div className="cell text-center">{`${voteItem.userId}`}</div>
                    <div className="cell text-center">{voteItem.regDate.replace(/(\d{4})(\d{2})(\d{2})/, "$1-$2-$3")}</div>
                  </div>
                ))}
              </div>
              {loading && (
                <div className="loading-indicator">
                  <div>로딩 중...</div>
                </div>
              )}
              <div className="bbs-footer-container" style={{ bottom: "56px" }}>
                <div className="bbs-button-container">
                  <button onClick={movingVotePage}>등록</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Bbs;
